import type { ComponentProps } from "react";
import { HAND_DOT, HAND_MARK_PATH, HAND_MORPH } from "@/features/copilot/lib/hand-mark-paths";
import { useReducedMotion } from "@/shared/hooks/use-reduced-motion";

type Props = Omit<ComponentProps<"svg">, "children"> & {
  /** Drop the dot into the palm, close and lift, release; the still mark under reduced motion. */
  animated?: boolean;
};

/// Hand's mark: a palm-up hand (🫴) holding something out for you. Drawn in `currentColor` so it
/// takes the text color around it.
export function HandMark({ animated = false, strokeWidth = 2, ...props }: Props) {
  const reducedMotion = useReducedMotion();
  const morph = animated && !reducedMotion;
  const { hand, dot, duration } = HAND_MORPH;
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <g>
        {morph ? (
          <animateTransform
            attributeName="transform"
            type="translate"
            dur={duration}
            repeatCount="indefinite"
            calcMode="spline"
            keyTimes={hand.keyTimes}
            keySplines={hand.keySplines}
            values={hand.lift}
          />
        ) : null}
        <path d={HAND_MARK_PATH}>
          {morph ? (
            <animate
              attributeName="d"
              dur={duration}
              repeatCount="indefinite"
              calcMode="spline"
              keyTimes={hand.keyTimes}
              keySplines={hand.keySplines}
              values={hand.values}
            />
          ) : null}
        </path>
        <circle
          cx={HAND_DOT.cx}
          cy={HAND_DOT.restY}
          r={HAND_DOT.r}
          fill="currentColor"
          stroke="none"
        >
          {morph ? (
            <>
              <animate
                attributeName="cy"
                dur={duration}
                repeatCount="indefinite"
                calcMode="spline"
                keyTimes={dot.keyTimes}
                keySplines={dot.keySplines}
                values={dot.y}
              />
              <animate
                attributeName="opacity"
                dur={duration}
                repeatCount="indefinite"
                keyTimes={dot.opacityTimes}
                values={dot.opacity}
              />
            </>
          ) : null}
        </circle>
      </g>
    </svg>
  );
}
